import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const Mission = () => {
  const { scrollYProgress } = useScroll();
  const imageY = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <div data-testid="mission-page" className="bg-[#0A0A0A] min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-[70vh] overflow-hidden">
        <motion.div
          style={{ y: imageY }}
          className="absolute inset-0"
        >
          <img
            src="https://i.imgur.com/UoqWlPm.jpg"
            alt="Rally Road"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </motion.div>

        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Oswald'] text-5xl sm:text-6xl md:text-8xl text-[#F2EDE6] text-shadow mb-4"
          >
            WHY WE RALLY.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-[#F2EDE6]/80 italic text-shadow"
          >
            This isn't just a hobby. This is a calling.
          </motion.p>
        </div>
      </section>

      {/* Story Content */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          {/* How It All Started */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="font-['Oswald'] text-3xl sm:text-4xl text-[#E8651A] mb-6">
              HOW IT ALL STARTED
            </h2>
            <p className="text-[#F2EDE6] text-lg leading-relaxed">
              Revline Rally started as Revline Meets — a series of car shows hosted throughout Houston. 
              The turnout was incredible. The energy was real. But we knew we could do more than park 
              cars in a lot.
            </p>
          </motion.div>

          {/* A Different Kind of Drive */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="font-['Oswald'] text-3xl sm:text-4xl text-[#E8651A] mb-6">
              A DIFFERENT KIND OF DRIVE
            </h2>
            <p className="text-[#F2EDE6] text-lg leading-relaxed">
              We didn't want to trek to a common place or an overcrowded restaurant. We wanted hidden 
              gems. Scenic routes. Small towns you've never heard of. Food spots you'd never find on 
              your own. The kind of experiences that only happen when you get behind the wheel and 
              actually go somewhere.
            </p>
          </motion.div>

          {/* The Community We're Building */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="font-['Oswald'] text-3xl sm:text-4xl text-[#E8651A] mb-6">
              THE COMMUNITY WE'RE BUILDING
            </h2>
            <p className="text-[#F2EDE6] text-lg leading-relaxed">
              Revline Rally isn't about showing off your car. It's about driving it. It's about the 
              person next to you at the fuel stop who becomes a friend for life. These rallies are 
              gatherings — of people who love cars, yes, but more than that, people who love life 
              and want to live it fully.
            </p>
          </motion.div>

          {/* Where We're Going */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <h2 className="font-['Oswald'] text-3xl sm:text-4xl text-[#E8651A] mb-6">
              WHERE WE'RE GOING
            </h2>
            <p className="text-[#F2EDE6] text-lg leading-relaxed">
              We've hosted 9+ events throughout Houston and counting. We're just getting started. 
              Multi-day rallies, scenic state drives, weekend escapes — the road ahead is wide open.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="py-16 px-6 bg-[#141414]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '9+', label: 'Events Hosted' },
              { value: '1,400+', label: 'Followers' },
              { value: '100%', label: 'Community Driven' },
              { value: 'Houston', label: 'TX Based' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <p className="font-['Oswald'] text-4xl md:text-5xl text-[#E8651A] mb-2">
                  {stat.value}
                </p>
                <p className="text-[#A19D98] text-sm tracking-wider uppercase">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Oswald'] text-3xl sm:text-4xl md:text-5xl text-[#F2EDE6] mb-8">
              READY TO JOIN THE FAMILY?
            </h2>
            <Link
              to="/rallies"
              data-testid="mission-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              See Upcoming Rallies
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Mission;
